/**
 * The goal picker's catalog — the five goals the empty home offers,
 * each with the band that answers it: the seats, the apps, and the brief the Maestro builds it from.
 *
 * Import-free on purpose (see the tests section in CLAUDE.md): the copy is i18n KEYS, the seats are the
 * bands.md folds by their English names, and the only function here builds a string.
 *
 * ## Where the five come from
 *
 * The LLM-classified core intents of non-Wix users (n=38,705, 2026-09): business setup 18%, content &
 * creative production 16%, social media 12%, business operations 11%, brand & marketing growth 6%. The
 * other bars — personal use (21%), customer communications (5%), automation (5%) — are not goals a band
 * answers, so the picker leaves them to "Just chat".
 *
 * ## Why these seats and these apps
 *
 * Read off the bands the Maestro actually created in September 2026 (683 `bandCard` rows in
 * `v1_conversation_message_crud`): three to five seats in 90% of them, the seats named "front of house"
 * (172), "content & social" (~250 across spellings), "money & reporting" (~150), "brand & design"
 * (~100), "store & orders" (~80) and "regulars & reviews" (~60); the apps gmail (551), instagram (513),
 * facebook (299), then a long tail. Every card below is a band that has been built many times, not a
 * guess at one. Names are English given names, gender-neutral per using-agents.md; the brief tells the
 * Maestro to rename them into the user's language when it hires. Every band has at least two seats,
 * because `ManageBand` lays a goal's tasks out only when a DIFFERENT agent can review each one — a
 * one-seat band would land without the task cards the card promises.
 *
 * WhatsApp / SMS / email are CHANNELS, not apps (`isMessagingChannel` in the harness refuses them), and the
 * calendar has its own connect link — so the ops card's "messages" and "calendar" chips are drawn as
 * glyphs, and the brief tells the Maestro to set them up through their own steps.
 */

export type GoalIntentId = 'setup' | 'content' | 'social' | 'ops' | 'brand';

/** What a "Connects to" chip stands for. Only `toolkit` is an app the band connects. */
export type GoalConnector =
  | { kind: 'toolkit'; toolkit: string; name: string }
  | { kind: 'calendar' }
  | { kind: 'messages' };

export interface GoalSeat {
  /** A gender-neutral English given name; its initial is the avatar, and the brief lets the Maestro rename it. */
  name: string;
  /** The bands.md fold this seat owns, for the brief. English, never rendered. */
  area: string;
}

export interface GoalIntent {
  id: GoalIntentId;
  titleKey: string;
  onelinerKey: string;
  badgeKey: string;
  bandNameKey: string;
  /** The visible opening message, in the user's voice. */
  seedKey: string;
  seats: readonly GoalSeat[];
  connectors: readonly GoalConnector[];
  /** The card and its band in English, for the hidden brief: the card's title, the band's name, the goal it is a bet on, and its first tasks. */
  brief: { title: string; band: string; goal: string; tasks: readonly string[] };
}

const key = (id: GoalIntentId, field: string) => `home.goals.${id}.${field}`;

function intent(
  id: GoalIntentId,
  seats: readonly GoalSeat[],
  connectors: readonly GoalConnector[],
  brief: GoalIntent['brief'],
): GoalIntent {
  return {
    id,
    titleKey: key(id, 'title'),
    onelinerKey: key(id, 'oneliner'),
    badgeKey: key(id, 'badge'),
    bandNameKey: key(id, 'band'),
    seedKey: key(id, 'seed'),
    seats,
    connectors,
    brief,
  };
}

const GMAIL: GoalConnector = { kind: 'toolkit', toolkit: 'gmail', name: 'Gmail' };
const INSTAGRAM: GoalConnector = { kind: 'toolkit', toolkit: 'instagram', name: 'Instagram' };
const FACEBOOK: GoalConnector = { kind: 'toolkit', toolkit: 'facebook', name: 'Facebook' };

export const GOAL_INTENTS: readonly GoalIntent[] = [
  intent(
    'setup',
    [
      { name: 'Sam', area: 'Front of house — every enquiry answered and chased, bookings confirmed' },
      { name: 'Ollie', area: 'Money & reporting — quotes, invoices, and one weekly read of the numbers' },
      { name: 'Robin', area: 'Content & social — a first steady rhythm of posts in their voice' },
    ],
    [GMAIL, { kind: 'calendar' }],
    {
      title: 'Set up my business',
      band: 'Setup band',
      goal: 'Get the business set up and running, with the first workflows in place',
      tasks: [
        'Set up the inbox: sort what is waiting and draft the first replies (front of house)',
        'Lay out the offers and prices so every enquiry gets a clear answer (money)',
        'Draft the first week of posts announcing the business (content)',
      ],
    },
  ),
  intent(
    'content',
    [
      { name: 'Casey', area: 'Content — posts, copy and captions in their voice' },
      { name: 'Drew', area: 'Brand & design — the visuals, images and video every piece of content runs on' },
    ],
    [],
    {
      title: 'Create content',
      band: 'Content band',
      goal: 'A steady stream of posts, copy, images and video, ready to publish',
      tasks: [
        'Write the first batch of posts and captions (content)',
        'Design the visuals for that batch (design)',
        'Propose a two-week content calendar for approval (content)',
      ],
    },
  ),
  intent(
    'social',
    [
      { name: 'Sage', area: 'Social media — a posting rhythm across every connected account, and reading what worked' },
      { name: 'Lee', area: 'Regulars & replies — comments and DMs answered, unhappy ones caught early' },
    ],
    [INSTAGRAM, FACEBOOK],
    {
      title: 'Grow on social',
      band: 'Social band',
      goal: 'Show up on social every week without thinking about it, and grow the audience',
      tasks: [
        'Read the connected accounts and propose a weekly posting rhythm (social)',
        'Draft the first week of posts (social)',
        'Answer the open comments and DMs, and flag the ones that need the owner (replies)',
      ],
    },
  ),
  intent(
    'ops',
    [
      { name: 'Oakley', area: 'Orders & bookings — the diary and the orders confirmed, prepped and reminded' },
      { name: 'Finley', area: 'Money & invoicing — invoiced on time, chased politely, reconciled' },
    ],
    [{ kind: 'calendar' }, { kind: 'messages' }],
    {
      title: 'Run day-to-day ops',
      band: 'Ops band',
      goal: 'Orders, bookings and invoices handled every day, with the owner hearing only the exceptions',
      tasks: [
        'Confirm and remind on this week’s bookings and orders (orders & bookings)',
        'List what is owed and send the overdue reminders (money)',
        'Set up the daily check-in on what needs the owner today (orders & bookings)',
      ],
    },
  ),
  intent(
    'brand',
    [
      { name: 'Blake', area: 'Brand identity — one coherent look across the site, profiles, invoices and print' },
      { name: 'Charlie', area: 'Content — the words that go with the look: tagline, bios, captions, in one voice' },
    ],
    [],
    {
      title: 'Build my brand',
      band: 'Brand band',
      goal: 'One coherent brand look and voice, everywhere the business shows up',
      tasks: [
        'Audit the current look across the site and profiles (brand)',
        'Propose the brand direction: palette, type and tone (brand)',
        'Write the tagline and bios in the new voice (content)',
      ],
    },
  ),
];

export function goalIntentById(id: string): GoalIntent | undefined {
  return GOAL_INTENTS.find((goal) => goal.id === id);
}

/** The apps a card actually connects, by slug — what the brief hands `ManageBand` as `apps`. */
export function goalIntentToolkits(goal: GoalIntent): string[] {
  return goal.connectors.flatMap((c) => (c.kind === 'toolkit' ? [c.toolkit] : []));
}

/**
 * The hidden brief that rides beside the seed as the turn's `context` — the same channel the "+ New
 * goal" seed uses for its workspace id. The user sees the seed; the Maestro also sees this.
 *
 * It exists because the seed alone leaves the Maestro on its onboarding path: two bands offered, a
 * pick asked for, the business interviewed first. The card has already made the pick, so the brief
 * says: this band, now, in one call, and ask afterwards.
 */
export function goalIntentContext(goal: GoalIntent): string {
  const seats = goal.seats.map((seat, i) => `${i + 1}. ${seat.name} — ${seat.area}`).join('\n');
  const toolkits = goalIntentToolkits(goal);
  const apps = toolkits.length ? toolkits.join(', ') : 'none — this band runs on no connected app';
  const calendar = goal.connectors.some((c) => c.kind === 'calendar')
    ? '\nThe calendar is not an app: after the band lands, offer its connect link the way onboarding.md does.'
    : '';
  const messages = goal.connectors.some((c) => c.kind === 'messages')
    ? '\nMessages (WhatsApp/SMS/email) are channels, not apps: never pass them to ManageBand; settle the outbound default in a later turn, per people-and-outreach.md.'
    : '';
  const tasks = goal.brief.tasks.map((task) => `- ${task}`).join('\n');
  return [
    `The user tapped the "${goal.brief.title}" card on the empty home ("What's your goal?"). They have already chosen; this turn builds it.`,
    `Create the "${goal.brief.band}" NOW, in this turn, with ManageBand action "create". Do not offer two bands, do not ask which they want, and do not interview them about the business first — the card is the pick. Compose it from bands.md with these seats:`,
    seats,
    `Give each seat a simple, gender-neutral given name common in the user's language (the names above are English suggestions; keep them only if the user writes English) and a title in their language. Pass a source_genome_slug only when best-practices/agent/ lists that spec; otherwise give the seat its role in your own words. Apps: ${apps}.${calendar}${messages}`,
    `Goal to lay out with the band, left waiting for their go: "${goal.brief.goal}". Its first tasks, one per seat that can act:`,
    tasks,
    `If knowledge/business or their site already says what the business is, use it for the space. If you know nothing about the business yet, still create the band now — name the space after the user for the moment — and ask what the business is in the same reply, after the band, as the one question. Reply short: what landed, who joined (name and area), the connect step if apps need connecting, then that one question.`,
  ].join('\n\n');
}
